// src/edu-world/systems/npc-relationship.ts
// NPC Relationship System — tracks evolving emotional bonds between child and NPCs
// Standalone module (Phase 3) — not wired into orchestrator yet

// ─── Types ───

export type PositiveInteractionType =
  | 'mission_completed'
  | 'greeting_visit'
  | 'return_visit_new_session'
  | 'micro_interaction_nearby';

export interface RelationshipLevelConfig {
  level: number;
  thresholdInteractions: number;
  unlockedDialogueSets: string[];
  unlockedAnimations: string[];
  affectionIndicator?: string;
}

export interface NPCRelationshipState {
  npcId: string;
  positiveInteractionCount: number;
  currentLevel: number;
  levelHistory: { level: number; reachedAt: number }[];
  lastInteraction: number;
  unlockedAffectionIndicators: string[];
}

export interface RelationshipFeedback {
  type: 'level_up' | 'positive_acknowledgment';
  newLevel?: number;
  affectionIndicator?: string;
  animationRef?: string;
}

// ─── Default Level Configuration ───

const DEFAULT_LEVEL_CONFIGS: RelationshipLevelConfig[] = [
  {
    level: 0,
    thresholdInteractions: 0,
    unlockedDialogueSets: ['basic-greeting'],
    unlockedAnimations: ['wave'],
    affectionIndicator: undefined,
  },
  {
    level: 1,
    thresholdInteractions: 2,
    unlockedDialogueSets: ['basic-greeting', 'warm-greeting'],
    unlockedAnimations: ['wave', 'happy-bounce'],
    affectionIndicator: 'small-heart-bubble',
  },
  {
    level: 2,
    thresholdInteractions: 5,
    unlockedDialogueSets: ['basic-greeting', 'warm-greeting', 'personal-greeting'],
    unlockedAnimations: ['wave', 'happy-bounce', 'excited-dance'],
    affectionIndicator: 'sparkle-around-npc',
  },
  {
    level: 3,
    thresholdInteractions: 10,
    unlockedDialogueSets: ['basic-greeting', 'warm-greeting', 'personal-greeting', 'memory-dialogue'],
    unlockedAnimations: ['wave', 'happy-bounce', 'excited-dance', 'hug-gesture'],
    affectionIndicator: 'rainbow-sparkle',
  },
];

// ─── NPCRelationshipSystem ───

export class NPCRelationshipSystem {
  private states: Map<string, NPCRelationshipState> = new Map();
  private levelConfigs: RelationshipLevelConfig[];

  constructor(levelConfigs?: RelationshipLevelConfig[]) {
    this.levelConfigs = levelConfigs ?? DEFAULT_LEVEL_CONFIGS;
  }

  /**
   * Record a positive interaction with an NPC.
   * Returns emotional feedback (always provided, regardless of level advancement).
   */
  recordInteraction(npcId: string, type: PositiveInteractionType): RelationshipFeedback {
    const state = this.getOrCreateState(npcId);
    state.positiveInteractionCount++;
    state.lastInteraction = Date.now();

    // Check for level advancement
    const advancedLevel = this.checkAndAdvanceLevel(state);

    if (advancedLevel !== null) {
      const levelConfig = this.levelConfigs[advancedLevel];
      return {
        type: 'level_up',
        newLevel: advancedLevel,
        affectionIndicator: levelConfig?.affectionIndicator,
        animationRef: levelConfig?.unlockedAnimations[levelConfig.unlockedAnimations.length - 1],
      };
    }

    // Every positive interaction gives emotional feedback
    const currentConfig = this.levelConfigs[state.currentLevel];
    const animations = currentConfig?.unlockedAnimations ?? ['wave'];
    return {
      type: 'positive_acknowledgment',
      animationRef: animations[Math.floor(Math.random() * animations.length)],
    };
  }

  /**
   * Get the current relationship level for an NPC (0-based).
   */
  getLevel(npcId: string): number {
    const state = this.states.get(npcId);
    return state?.currentLevel ?? 0;
  }

  /**
   * Get the total positive interaction count for an NPC.
   */
  getInteractionCount(npcId: string): number {
    const state = this.states.get(npcId);
    return state?.positiveInteractionCount ?? 0;
  }

  /**
   * Get the full relationship state for an NPC.
   */
  getState(npcId: string): NPCRelationshipState {
    return this.getOrCreateState(npcId);
  }

  /**
   * Get unlocked dialogue sets for an NPC based on current level.
   */
  getAvailableDialogueSets(npcId: string): string[] {
    const level = this.getLevel(npcId);
    const config = this.levelConfigs[level];
    return config?.unlockedDialogueSets ?? ['basic-greeting'];
  }

  /**
   * Get unlocked animations for an NPC based on current level.
   */
  getAvailableAnimations(npcId: string): string[] {
    const level = this.getLevel(npcId);
    const config = this.levelConfigs[level];
    return config?.unlockedAnimations ?? ['wave'];
  }

  /**
   * Get the level configuration array (for external inspection).
   */
  getLevelConfigs(): ReadonlyArray<RelationshipLevelConfig> {
    return this.levelConfigs;
  }

  // ─── Private ───

  private getOrCreateState(npcId: string): NPCRelationshipState {
    let state = this.states.get(npcId);
    if (!state) {
      state = {
        npcId,
        positiveInteractionCount: 0,
        currentLevel: 0,
        levelHistory: [{ level: 0, reachedAt: Date.now() }],
        lastInteraction: 0,
        unlockedAffectionIndicators: [],
      };
      this.states.set(npcId, state);
    }
    return state;
  }

  private checkAndAdvanceLevel(state: NPCRelationshipState): number | null {
    const nextLevel = state.currentLevel + 1;
    if (nextLevel >= this.levelConfigs.length) {
      return null; // Already at max level
    }

    const nextConfig = this.levelConfigs[nextLevel];
    if (state.positiveInteractionCount >= nextConfig.thresholdInteractions) {
      state.currentLevel = nextLevel;
      state.levelHistory.push({ level: nextLevel, reachedAt: Date.now() });

      if (nextConfig.affectionIndicator) {
        state.unlockedAffectionIndicators.push(nextConfig.affectionIndicator);
      }

      return nextLevel;
    }

    return null;
  }
}
